import tracker from 'sanitas-tracking'

import useContentsquare from '@/hooks/useContentsquare'
import { logError } from '@/helpers/Logger'
import { events$ } from '@/services'
import { CookieUtil } from '@/utils/Cookie'
import sessionStore from '@/store/session'
import leadStore from '@/store/lead'

import { EVENT_TRACKING, EVENT_PET, EVENT_LEAD, EVENT_MODAL, EVENT_PRODUCT, EVENT_SESSION } from '@/config/events'
import { PET_ROUTES } from '@/config/routes'
import { RULES, FUNNELED_RULES } from '@/config/tracker'
import { DEDUCTIBLE_OPTIONS } from '@/config/petConstants'

// HOOKS
const { triggerCSPageView } = useContentsquare()

// DATA
const PARAMS_TO_REMOVE = ['leadCode', 'salesPartnerNumber']
const PARAM_PATTERN = ':?__KEY__(\\(.*\\))?\\??'

// METHODS
function anonymizeRoute(route) {
  if (!route) {
    return
  }

  const matched = route.matched[route.matched.length - 1]
  let path = matched?.path

  const params = Object.entries(route.params).reduce((__params, [key, value]) => {
    const re = new RegExp(PARAM_PATTERN.replace('__KEY__', key))

    if (PARAMS_TO_REMOVE.includes(key)) {
      path = path.replace(re, '')
      return __params
    }

    if (!value) {
      path = path.replace(re, '')
      return __params
    }

    __params[key] = value
    path = path.replace(re, value)

    return __params
  }, {})

  path = path?.replace(/\/+/g, '/').replace(/(.*)\/$/, '$1')

  return {
    ...route,
    params,
    path,
  }
}

export function trackAction(rule, skipHistory, params) {
  if (!rule) throw new Error('Rule parameter missing')
  const forced = !!params?.forced

  // FUNNELED_RULES are only triggered once, unless `force` is set truthy
  if (
    !forced &&
    Object.values(FUNNELED_RULES).includes(rule) &&
    !!tracker.getHistory().find(item => item.event === rule)
  ) {
    return
  }
  tracker.trigger(tracker.triggerType.ACTION, rule, skipHistory, params)
}


function clearTrackingHistory() {
  if (tracker.clearHistory) {
    tracker.clearHistory()
  } else {
    window.Sanitas.Tracking.history = []
  }
}

function trackEntered(opts = { source: undefined, strict: false }) {
  const key = 'sanitas-dvp-pet_entered-source'
  if (opts.source) {
    // write to sessionStorage
    CookieUtil.set({
      key,
      value: opts.source,
    })
  } else {
    const _source = CookieUtil.read(key)
    if (_source) {
      tracker.set({
        data: {
          petScreenSource: _source,
        },
      })
      CookieUtil.delete(key)
    }

    // if strict is not truthy, then a source has to be set!
    if ((opts.strict && _source) || !opts.strict) {
      trackAction(RULES.PET_ENTERED)
    }
  }
}

export function trackRoute(to, from) {
  const isLanguageSet = tracker.get('page.language')

  if (!isLanguageSet) {
    tracker.set({
      data: { page: { language: document.documentElement.lang } },
    })
  }

  setSessionDataInTracker(to)

  triggerPageView(to, from)
}

function triggerPageView(to, from) {
  const route = to && anonymizeRoute(to)
  const routeFrom = from && anonymizeRoute(from)

  // Trigger page view for every route change where path differs
  const shouldTriggerPageView = route?.path !== routeFrom?.path

  if (shouldTriggerPageView) {
    const language = tracker.get('page.language')
    const host = window.location.origin
    const baseAliasUrl = host
    const baseUrl = host + window.location.pathname.replace(/(.*)\/$/, '$1')
    const url = baseUrl
    const aliasUrl = `${baseAliasUrl}/de${route.path.substring(3)}`
    const pageName = route.name
    const pageTitle = pageName ? pageName[0].toUpperCase() + pageName.slice(1) : null

    const data = {
      page: {
        url,
        aliasUrl,
        name: `/${pageName}`,
        route: pageTitle,
        containingPage: pageName,
        title: pageTitle,
        description: `Pet - ${language.toUpperCase()} - ${pageTitle}`,
        descriptionWithoutLang: `Pet - ${pageTitle}`,
      },
    }

    tracker.set({ data })
    tracker.trigger(tracker.triggerType.VIEW, RULES.PET_PAGE_VIEWED)

    triggerRouteBasedActions(to, from)
  }
}

/**
 * Triggers funnel rules and other route based actions
 * @param to
 * @param _from
 */
function triggerRouteBasedActions(to, _from) {
  if (to?.name) {
    switch (to.name) {
      case PET_ROUTES.ENTRY.name:
        trackAction(RULES.PET_SCREEN_ENTRY_FUNNELED)
        break

      case PET_ROUTES.ENTRY_WITH_SALES_PARTNER_NUMBER.name:
        trackAction(RULES.PET_SCREEN_ENTRY_WITH_SALES_PARTNER_NUMBER_FUNNELED)
        break

      case PET_ROUTES.REENTRY.name:
        trackAction(RULES.PET_SCREEN_REENTRY_FUNNELED)
        break

      case PET_ROUTES.PET_DETAILS.name:
        trackAction(RULES.PET_SCREEN_PET_DETAILS_FUNNELED)
        break

      case PET_ROUTES.HEALTH_QUESTIONS.name:
        trackAction(RULES.PET_SCREEN_HEALTH_QUESTIONS_FUNNELED)
        break
        
      case PET_ROUTES.PRODUCTS_SELECTION.name:
        trackAction(RULES.PET_SCREEN_PRODUCTS_SELECTION_FUNNELED)
        break

      case PET_ROUTES.OVERVIEW.name:
        trackAction(RULES.PET_SCREEN_OVERVIEW_FUNNELED)
        break

      case PET_ROUTES.SUBMIT.name:
        trackAction(RULES.PET_SCREEN_SUBMIT_FUNNELED)
        break

      case PET_ROUTES.SUCCESS.name:
        trackAction(RULES.PET_SCREEN_SUCCESS_FUNNELED)
        break

      case PET_ROUTES.UNSUBSCRIBE.name:
        trackAction(RULES.PET_SCREEN_UNSUBSCRIBE_FUNNELED)
        break
    }
  }
}

function setTrackingSourceComponent(sourceComponent) {
  if (!sourceComponent) {
    logError(['sourceComponent falsy', sourceComponent])
    return
  }
  tracker.set({ data: { sourceComponent } })
}

function setSessionDataInTracker(to) {
  const randomSessionId = sessionStore.getRandomSessionId()
  const leadCode = sessionStore.session.leadCode || to?.params?.leadCode
  
  const leadData = leadStore.lead
  const petData = leadData?.policyData?.petData

  const sessionData = {}
  
  if (randomSessionId) {
    sessionData.randomSessionId = randomSessionId
  }

  const existingPetData = tracker.get('petData') || {}
  const leadgiver = sessionStore?.getLeadgiver()

  sessionData.petData = {
    ...(petData && Object.keys(petData).length > 0 ? petData : {}),
    leadCode,
    salesPartnerNumber: sessionStore?.getSalesPartnerNumber(),
    flowType: sessionStore?.getFlowType(),
    ...(leadgiver && { leadgiver }),
    ...(existingPetData.product && { product: existingPetData.product })
  }

  if (Object.keys(sessionData).length > 0) {
    tracker.set({
      data: sessionData
    })
  }
}

function install(app) {
  if (import.meta.env.VITE_TRACKER_LOGGING) {
    window.Sanitas.Tracking.loggable = true
  }

  if (import.meta.env.VITE_TRACKER_ENABLED) {
    window.Sanitas.Tracking.enabled = true
  }

  tracker.set({
    data: { _version: { fe: { pet: app._component.__name } } },
  })

  setSessionDataInTracker()

  // Modals
  events$.on(EVENT_MODAL.OPEN, ({ modalName }) => {
    tracker.set({ data: { modalName } })
    trackAction(RULES.PET_MODAL_OPENED_FUNNELED)
  })

  events$.on(EVENT_MODAL.CLOSE, ({ modalName }) => {
    tracker.set({ data: { modalName } })
    trackAction(RULES.PET_MODAL_CLOSE)
  })


  events$.on(EVENT_TRACKING.ROUTE_CHANGE, params => {
    trackEntered(params.opts)
    trackRoute(params.to, params.from)

    triggerCSPageView()
  })

  events$.on(EVENT_TRACKING.CLEAR_HISTORY, clearTrackingHistory)

  events$.on(EVENT_TRACKING.ENTERED, trackEntered)

  events$.on(EVENT_TRACKING.SET, data => {
    tracker.set({
      data: {
        ...data,
      },
    })
  })

  events$.on(EVENT_SESSION.UPDATE, ({ randomSessionId }) => {
    const sessionData = {}
    
    if (randomSessionId) {
      sessionData.randomSessionId = randomSessionId
    }
    
    if (Object.keys(sessionData).length > 0) {
      tracker.set({
        data: sessionData
      })
    }
  })

  events$.on(EVENT_LEAD.UPDATED, () => {
    setSessionDataInTracker()
  })

  events$.on(EVENT_PET.GOTO_SANITAS, () => {
    tracker.trigger(tracker.triggerType.ACTION, RULES.PET_GO_TO_SANITAS_CLICKED)
  })

  events$.on(EVENT_PET.NEW_PET_LEAD, () => {
    // First completely clear the existing session
    sessionStore.clearSession({ silent: true })
    
    // Then activate a fresh session with new randomSessionId
    sessionStore.activateSession()
    const newRandomSessionId = sessionStore.getRandomSessionId()
    
    // Clear tracking history so events can be sent again
    clearTrackingHistory()
    
    // Reset petData and set new session data
    tracker.set({
      data: { 
        petData: null,
        randomSessionId: newRandomSessionId
      }
    })
    tracker.trigger(tracker.triggerType.ACTION, RULES.PET_NEW_PET_CLICKED)
  })

  events$.on(EVENT_PET.SELECTED, (petData) => {
    tracker.set({
      data: { petData }
    })
    trackAction(RULES.PET_SELECTED)
  })
  
  events$.on(EVENT_LEAD.HEALTH_QUESTIONS_EXTENDED, () => {
    trackAction(RULES.PET_SCREEN_HEALTH_QUESTIONS_EXTENDED_FUNNELED)
  })

  events$.on(EVENT_LEAD.REJECTED, () => {
    trackAction(RULES.PET_SCREEN_HEALTH_QUESTIONS_DECLINED_FUNNELED)
  })

  events$.on(EVENT_LEAD.SUMMARY_SENT, () => {
    trackAction(RULES.PET_SUMMARY_SENT_FUNNELED)
  })

  events$.on(EVENT_PRODUCT.ADDED, ({ deductible, price, tariffVariant }) => {
    const currentPetData = tracker.get('petData') || {}
    const updatedPetData = {
      ...currentPetData,
      product: {
        ...currentPetData.product,
        tariffVariant,
        deductible: DEDUCTIBLE_OPTIONS[deductible].value,
        ...(price && { price })
      }
    }
    
    tracker.set({
      data: {
        petData: updatedPetData
      }
    })
    trackAction(RULES.PET_PRODUCT_ADDED)
  })

  events$.on(EVENT_PRODUCT.DEDUCTIBLE_CHANGED, ({ deductible, source, price }) => {
    setTrackingSourceComponent(source)
    
    const currentPetData = tracker.get('petData') || {}
    const updatedPetData = {
      ...currentPetData,
      product: {
        ...currentPetData.product,
        deductible: DEDUCTIBLE_OPTIONS[deductible].value,
        ...(price && { price })
      }
    }
    
    tracker.set({
      data: {
        petData: updatedPetData
      }
    })
    trackAction(RULES.PET_DEDUCTIBLE_CHANGED)
  })
}

export default {
  install
}